//@ts-nocheck
export const DISCOUNT_TYPE_FIXED = 'FIXED';
export const DISCOUNT_TYPE_PERCENTAGE = 'PERCENTAGE';
export const DISCOUNT_SCOPE_BOOKING = 'booking';
export const DISCOUNT_SCOPE_PERSON = 'person';
export const EXPIRATION_MODE_DATE = 'date';
export const EXPIRATION_MODE_DAYS = 'days';

const valueFrom = (source, keys, fallback = undefined) => {
  if (!source) return fallback;

  for (const key of keys) {
    if (source[key] !== undefined) return source[key];
  }

  return fallback;
};

const toBoolean = (value, fallback = false) => {
  if (value === undefined || value === null) return fallback;
  return Boolean(value);
};

export const extractDiscountCodeItems = (response) => {
  if (Array.isArray(response)) return response;
  if (Array.isArray(response?.items)) return response.items;
  if (Array.isArray(response?.data?.items)) return response.data.items;
  if (Array.isArray(response?.discountCodes)) return response.discountCodes;
  if (Array.isArray(response?.data?.discountCodes)) {
    return response.data.discountCodes;
  }
  return [];
};

export const normalizeDiscountCode = (discountCode = {}) => {
  const discountType = String(
    valueFrom(
      discountCode,
      ['discountType', 'discounttype', 'DiscountType'],
      ''
    )
  ).toUpperCase();
  const expType = valueFrom(discountCode, ['expType', 'exptype', 'ExpType']);

  return {
    discountCodeId: valueFrom(discountCode, [
      'discountCodeId',
      'discountcodeid',
      'DiscountCodeID',
      'id'
    ]),
    title: valueFrom(discountCode, ['title', 'Title'], ''),
    code: valueFrom(discountCode, ['code', 'Code'], ''),
    discountType:
      discountType === DISCOUNT_TYPE_PERCENTAGE
        ? DISCOUNT_TYPE_PERCENTAGE
        : DISCOUNT_TYPE_FIXED,
    discountValue: valueFrom(discountCode, [
      'discountValue',
      'discountvalue',
      'DiscountValue'
    ]),
    scope:
      valueFrom(discountCode, ['scope', 'Scope']) || DISCOUNT_SCOPE_BOOKING,
    inclAddons: toBoolean(
      valueFrom(discountCode, ['inclAddons', 'incladdons', 'InclAddons']),
      false
    ),
    combinable: toBoolean(
      valueFrom(discountCode, ['combinable', 'Combinable']),
      false
    ),
    defaultOnNewTrips: toBoolean(
      valueFrom(discountCode, [
        'defaultOnNewTrips',
        'defaultonnewtrips',
        'DefaultOnNewTrips'
      ]),
      false
    ),
    availabilityLimit: valueFrom(discountCode, [
      'availabilityLimit',
      'availabilitylimit',
      'AvailabilityLimit'
    ]),
    usedCount: valueFrom(discountCode, ['usedCount', 'usedcount', 'UsedCount']),
    expType:
      expType === EXPIRATION_MODE_DATE || expType === EXPIRATION_MODE_DAYS
        ? expType
        : null,
    expirationDateTime: valueFrom(discountCode, [
      'expirationDateTime',
      'expirationdatetime',
      'ExpirationDateTime'
    ]),
    expDays: valueFrom(discountCode, ['expDays', 'expdays', 'ExpDays'])
  };
};

export const getDiscountCodeId = (discountCode = {}) =>
  normalizeDiscountCode(discountCode).discountCodeId;

export const createEmptyDiscountCodeForm = () => ({
  title: '',
  code: '',
  discountType: DISCOUNT_TYPE_FIXED,
  discountValue: '',
  scope: DISCOUNT_SCOPE_BOOKING,
  inclAddons: false,
  combinable: false,
  defaultOnNewTrips: false,
  availabilityLimit: null,
  expMode: EXPIRATION_MODE_DATE,
  expirationDate: '',
  expDays: ''
});

export const createDiscountCodeFormFromItem = (discountCode) => {
  const normalized = normalizeDiscountCode(discountCode);
  const expirationDate =
    normalized.expirationDateTime &&
    String(normalized.expirationDateTime).split('T')[0];

  return {
    title: normalized.title || '',
    code: normalized.code || '',
    discountType: normalized.discountType,
    discountValue:
      normalized.discountValue === null ||
      normalized.discountValue === undefined
        ? ''
        : String(Number(normalized.discountValue)),
    scope: normalized.scope || DISCOUNT_SCOPE_BOOKING,
    inclAddons: normalized.inclAddons,
    combinable: normalized.combinable,
    defaultOnNewTrips: normalized.defaultOnNewTrips,
    availabilityLimit: normalized.availabilityLimit ?? null,
    expMode: normalized.expType,
    expirationDate: expirationDate || '',
    expDays:
      normalized.expDays === null || normalized.expDays === undefined
        ? ''
        : String(normalized.expDays)
  };
};

export const buildEditableDiscountCodePayload = (formValues) => {
  const isPercentage = formValues.discountType === DISCOUNT_TYPE_PERCENTAGE;
  const discountValue = Number(formValues.discountValue);
  const expDays = Number(formValues.expDays);
  const expMode = formValues.expMode;

  return {
    title: formValues.title.trim(),
    code: formValues.code.trim().toUpperCase(),
    discountType: formValues.discountType,
    discountValue: Number.isFinite(discountValue) ? discountValue : 0,
    scope: isPercentage ? null : formValues.scope || DISCOUNT_SCOPE_BOOKING,
    inclAddons: isPercentage ? Boolean(formValues.inclAddons) : false,
    combinable: Boolean(formValues.combinable),
    defaultOnNewTrips: Boolean(formValues.defaultOnNewTrips),
    availabilityLimit: formValues.availabilityLimit ?? null,
    expType:
      expMode === EXPIRATION_MODE_DATE || expMode === EXPIRATION_MODE_DAYS
        ? expMode
        : null,
    expirationDateTime:
      expMode === EXPIRATION_MODE_DATE && formValues.expirationDate
        ? `${formValues.expirationDate}T23:59:59Z`
        : null,
    expDays:
      expMode === EXPIRATION_MODE_DAYS && Number.isFinite(expDays)
        ? expDays
        : null,
    notes: formValues?.notes?.trim() || null
  };
};

export const formatCompactNumber = (value) => {
  const numberValue = Number(value || 0);
  if (!Number.isFinite(numberValue)) return '0';
  return Number.isInteger(numberValue)
    ? String(numberValue)
    : numberValue.toFixed(2).replace(/\.?0+$/, '');
};

export const formatDiscountValue = (discountCode) => {
  const normalized = normalizeDiscountCode(discountCode);
  const amount = Number(normalized.discountValue || 0);

  if (normalized.discountType === DISCOUNT_TYPE_PERCENTAGE) {
    return `${formatCompactNumber(amount)}%`;
  }

  return `$${formatCompactNumber(amount)}`;
};
